import { useState } from 'react'
import type { ServiceLevel } from '../types'
import { CommentModal, ScheduleModal, AccessibilityModal } from './BottomSheet'
import { getCarIcon } from '../assets/cars'

// Company color for cars - purple gradient
const COMPANY_COLOR = '#9362CC'

// Информация о тарифах
const TARIFF_INFO: Record<string, { 
  name: string
  description: string
  carModels: string[]
  maxPassengers: number
  isPremium: boolean
}> = {
  econom: { 
    name: 'Эконом', 
    description: 'Недорогие поездки на каждый день',
    carModels: ['Kia Rio', 'Hyundai Solaris', 'Volkswagen Polo'],
    maxPassengers: 4,
    isPremium: false,
  },
  comfort: { 
    name: 'Комфорт', 
    description: 'На работу и по делам',
    carModels: ['Skoda Octavia', 'Kia K5', 'Hyundai Sonata'],
    maxPassengers: 4,
    isPremium: false,
  },
  comfortplus: { 
    name: 'Комфорт+', 
    description: 'Расслабиться в долгой поездке',
    carModels: ['Toyota Camry', 'Kia K5', 'Skoda Superb'],
    maxPassengers: 4,
    isPremium: false,
  },
  business: { 
    name: 'Business', 
    description: 'Премиум на каждый день',
    carModels: ['Mercedes E-class', 'BMW 5', 'Audi A6'],
    maxPassengers: 3,
    isPremium: true,
  },
  premier: { 
    name: 'Premier', 
    description: 'Представительские авто',
    carModels: ['Mercedes S-class', 'BMW 7', 'Audi A8'],
    maxPassengers: 3,
    isPremium: true,
  },
  elite: { 
    name: 'Elite', 
    description: 'Машины класса люкс',
    carModels: ['Mercedes-Maybach', 'BMW 7 L', 'Rolls-Royce'],
    maxPassengers: 3,
    isPremium: true,
  },
  child_tariff: { 
    name: 'Детский', 
    description: 'Если вы с ребёнком',
    carModels: ['Skoda Octavia', 'Kia K5'],
    maxPassengers: 4,
    isPremium: false,
  },
}

// Иконки опций
const DoorIcon = () => (
  <svg width="32" height="32" viewBox="0 0 48 48" fill="none">
    <rect x="12" y="6" width="24" height="36" rx="3" stroke="currentColor" strokeWidth="2" fill="none"/>
    <circle cx="30" cy="24" r="2" fill="currentColor"/>
  </svg>
)

const SilenceIcon = () => (
  <svg width="32" height="32" viewBox="0 0 48 48" fill="none">
    <path d="M22 12L14 18H8v12h6l8 6V12z" fill="currentColor" opacity="0.2"/>
    <path d="M22 12L14 18H8v12h6l8 6V12z" stroke="currentColor" strokeWidth="2" fill="none"/>
    <line x1="32" y1="18" x2="40" y2="30" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="40" y1="18" x2="32" y2="30" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
  </svg>
)

const ChildSeat1Icon = () => (
  <svg width="32" height="32" viewBox="0 0 48 48" fill="none">
    <ellipse cx="24" cy="14" rx="6" ry="5" fill="currentColor" opacity="0.3"/>
    <path d="M12 42V32c0-6 5-10 12-10s12 4 12 10v10" stroke="currentColor" strokeWidth="2" fill="none"/>
    <path d="M18 32v6m12-6v6" stroke="currentColor" strokeWidth="2"/>
    <circle cx="24" cy="14" r="5" stroke="currentColor" strokeWidth="2" fill="none"/>
  </svg>
)

const ChildSeat2Icon = () => (
  <svg width="32" height="32" viewBox="0 0 48 48" fill="none">
    <rect x="10" y="20" width="28" height="20" rx="4" stroke="currentColor" strokeWidth="2" fill="none"/>
    <path d="M18 20v-4a6 6 0 0112 0v4" stroke="currentColor" strokeWidth="2"/>
    <path d="M24 28v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
)

const PetIcon = () => (
  <svg width="32" height="32" viewBox="0 0 48 48" fill="none">
    <ellipse cx="24" cy="28" rx="10" ry="8" fill="currentColor" opacity="0.2"/>
    <ellipse cx="24" cy="28" rx="10" ry="8" stroke="currentColor" strokeWidth="2" fill="none"/>
    <ellipse cx="14" cy="18" rx="3" ry="4" stroke="currentColor" strokeWidth="2" fill="none"/>
    <ellipse cx="34" cy="18" rx="3" ry="4" stroke="currentColor" strokeWidth="2" fill="none"/>
    <ellipse cx="10" cy="12" rx="2.5" ry="3" stroke="currentColor" strokeWidth="1.5" fill="none"/>
    <ellipse cx="38" cy="12" rx="2.5" ry="3" stroke="currentColor" strokeWidth="1.5" fill="none"/>
  </svg>
)

const BikeIcon = () => (
  <svg width="32" height="32" viewBox="0 0 48 48" fill="none">
    <circle cx="12" cy="30" r="8" stroke="currentColor" strokeWidth="2" fill="none"/>
    <circle cx="36" cy="30" r="8" stroke="currentColor" strokeWidth="2" fill="none"/>
    <path d="M12 30l8-12h8l8 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M20 18V8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M16 8h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
)

interface OrderOption {
  id: string
  icon: React.ReactNode
  title: string
  subtitle?: string
}

const ORDER_OPTIONS: OrderOption[] = [
  { id: 'door', icon: <DoorIcon />, title: 'Открою дверь сам' },
  { id: 'silence', icon: <SilenceIcon />, title: 'Поездка в тишине' },
  { id: 'child_seat_1', icon: <ChildSeat1Icon />, title: 'Первое кресло' },
  { id: 'child_seat_2', icon: <ChildSeat2Icon />, title: 'Второе кресло' },
  { id: 'pet', icon: <PetIcon />, title: 'С питомцем' },
  { id: 'bike', icon: <BikeIcon />, title: 'Велосипед', subtitle: 'Минивэн' },
]

interface TariffDetailsProps {
  tariff: ServiceLevel
  fromAddress?: string
  toAddress?: string
  onClose: () => void
  onOrder: () => void
}

export const TariffDetails = ({ 
  tariff, 
  fromAddress, 
  toAddress, 
  onClose, 
  onOrder 
}: TariffDetailsProps) => {
  const [selectedOptions, setSelectedOptions] = useState<Set<string>>(new Set())
  const [comment, setComment] = useState('')
  const [scheduledDate, setScheduledDate] = useState<Date | null>(null)
  const [accessibilityOptions, setAccessibilityOptions] = useState({
    helpFindCar: false,
    guidedog: false,
    textOnly: false,
    deaf: false,
    wheelchair: false,
  })
  
  // Модальные окна
  const [showCommentModal, setShowCommentModal] = useState(false)
  const [showScheduleModal, setShowScheduleModal] = useState(false)
  const [showAccessibilityModal, setShowAccessibilityModal] = useState(false)
  
  const info = TARIFF_INFO[tariff.class] || { 
    name: tariff.class, 
    description: '',
    carModels: [],
    maxPassengers: 4,
    isPremium: false,
  }
  
  const isPremium = info.isPremium
  
  // Get the car SVG component for this tariff class
  const CarIcon = getCarIcon(tariff.class)
  
  const toggleOption = (id: string) => {
    const newOptions = new Set(selectedOptions)
    if (newOptions.has(id)) {
      newOptions.delete(id)
    } else {
      newOptions.add(id)
    }
    setSelectedOptions(newOptions)
  }

  // Цвета темы в зависимости от тарифа
  const theme = isPremium ? {
    bg: 'bg-dark',
    headerBg: 'from-dark-modal to-dark',
    cardBg: 'bg-dark-secondary',
    buttonBg: 'bg-gradient-to-r from-amber-500 to-amber-600',
    accent: 'text-amber-400',
    border: 'border-amber-500/20',
  } : {
    bg: 'bg-dark',
    headerBg: 'from-dark-secondary to-dark',
    cardBg: 'bg-dark-secondary',
    buttonBg: 'bg-gradient-primary',
    accent: 'text-primary',
    border: 'border-primary/10',
  }

  return (
    <div className={`fixed inset-0 z-50 flex flex-col ${theme.bg} animate-fade-in overflow-hidden`}>
      {/* Header с изображением */}
      <div className="relative">
        {/* Кнопка закрытия */}
        <button
          onClick={onClose}
          className={`absolute top-3 sm:top-4 left-3 sm:left-4 w-9 h-9 sm:w-10 sm:h-10 rounded-full ${isPremium ? 'bg-black/50' : 'bg-dark/80'} backdrop-blur shadow-lg flex items-center justify-center z-20`}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={isPremium ? 'text-white' : 'text-white'}>
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>

        {/* Маршрут */}
        {fromAddress && toAddress && (
          <div className="absolute top-3 sm:top-4 right-3 sm:right-4 left-14 sm:left-16 text-right z-20">
            <p className={`text-xs sm:text-sm truncate font-medium ${isPremium ? 'text-white' : 'text-white'}`}>{fromAddress}</p>
            <p className={`text-[10px] sm:text-xs truncate ${isPremium ? 'text-text-secondary' : 'text-text-secondary'}`}>{toAddress}</p>
          </div>
        )}

        {/* Фото машины - SVG в фирменном цвете */}
        <div className={`h-56 sm:h-72 bg-gradient-to-b ${theme.headerBg} relative overflow-hidden`}>
          <div className="flex items-center justify-center h-full pt-10">
            <div className="transform scale-[2] sm:scale-[2.5] drop-shadow-2xl">
              <CarIcon 
                size={120} 
                color={isPremium ? undefined : COMPANY_COLOR} 
              />
            </div>
          </div>
        </div>

        {/* Название и цена */}
        <div className="px-4 pt-4 pb-2">
          <div className="flex items-start justify-between">
            <div>
              <h1 className={`text-xl sm:text-2xl font-bold ${isPremium ? 'text-white' : 'text-white'}`}>{info.name}</h1>
              <p className={`text-xs sm:text-sm mt-0.5 flex items-center gap-2 ${isPremium ? 'text-text-secondary' : 'text-text-secondary'}`}>
                {info.description}
                <span className="flex items-center gap-1">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                  {info.maxPassengers}
                </span>
              </p>
            </div>
            <div className="text-right">
              <p className={`text-xl sm:text-2xl font-bold ${isPremium ? 'text-white' : 'text-white'}`}>{tariff.price_rub} ₽</p>
              <p className={`text-xs sm:text-sm ${isPremium ? 'text-text-secondary' : 'text-text-secondary'}`}>
                {tariff.estimated_waiting?.message || '~5 мин'}
              </p>
            </div>
          </div>
          
          {/* Модели машин */}
          {info.carModels.length > 0 && (
            <p className={`text-xs mt-2 ${isPremium ? 'text-text-muted' : 'text-text-muted'}`}>
              {info.carModels.join(' · ')}
            </p>
          )}
        </div>
      </div>

      {/* Контент */}
      <div className={`flex-1 overflow-y-auto ${theme.cardBg}`}>
        {/* Быстрые опции */}
        <div className={`px-4 py-4 ${isPremium ? 'bg-dark-tertiary' : 'bg-dark-tertiary'}`}>
          <div className="flex gap-2.5 overflow-x-auto hide-scrollbar -mx-4 px-4 pb-1">
            {ORDER_OPTIONS.map((option) => {
              const isSelected = selectedOptions.has(option.id)
              return (
                <button
                  key={option.id}
                  onClick={() => toggleOption(option.id)}
                  className={`flex-shrink-0 flex flex-col items-center p-3 rounded-xl border transition-all w-[76px] sm:w-20 ${
                    isSelected
                      ? isPremium 
                        ? 'bg-amber-500/10 border-amber-500/30 text-amber-400' 
                        : 'bg-primary/10 border-primary/30 text-primary'
                      : isPremium
                        ? 'bg-dark-modal border-white/10 text-text-secondary'
                        : 'bg-dark-secondary border-primary/10 text-text-secondary'
                  }`}
                >
                  <div className="mb-2">
                    {option.icon}
                  </div>
                  <p className="text-[10px] sm:text-xs text-center leading-tight font-medium">
                    {option.title}
                  </p>
                  {option.subtitle && (
                    <p className={`text-[9px] mt-0.5 flex items-center gap-0.5 ${isPremium ? 'text-text-muted' : 'text-text-muted'}`}>
                      {option.subtitle}
                      <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 18l6-6-6-6"/>
                      </svg>
                    </p>
                  )}
                  {isSelected && (
                    <div className={`w-4 h-4 mt-1.5 rounded-full ${isPremium ? 'bg-amber-500' : 'bg-primary'} flex items-center justify-center`}>
                      <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </div>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        {/* Дополнительные действия */}
        <div className={`${isPremium ? 'bg-dark-tertiary' : 'bg-dark-tertiary'} mt-2`}>
          {/* Комментарий */}
          <button 
            onClick={() => setShowCommentModal(true)}
            className={`w-full flex items-center gap-4 px-4 py-4 border-b ${isPremium ? 'border-white/10' : 'border-primary/10'} transition-colors`}
          >
            <div className={`w-10 h-10 rounded-full ${isPremium ? 'bg-dark-modal' : 'bg-dark-secondary'} flex items-center justify-center ${isPremium ? 'text-text-secondary' : 'text-text-secondary'}`}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
              </svg>
            </div>
            <div className="flex-1 text-left">
              <span className={`text-sm sm:text-base ${isPremium ? 'text-white' : 'text-text-primary'}`}>Комментарий водителю</span>
              {comment && (
                <p className={`text-xs truncate ${isPremium ? 'text-text-muted' : 'text-text-muted'}`}>{comment}</p>
              )}
            </div>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={isPremium ? 'text-text-muted' : 'text-text-muted'}>
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </button>

          {/* Заказ другому */}
          <button className={`w-full flex items-center gap-4 px-4 py-4 border-b ${isPremium ? 'border-white/10' : 'border-primary/10'} transition-colors`}>
            <div className={`w-10 h-10 rounded-full ${isPremium ? 'bg-dark-modal' : 'bg-dark-secondary'} flex items-center justify-center ${isPremium ? 'text-text-secondary' : 'text-text-secondary'}`}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="7" r="4"/>
                <path d="M3 21v-2a4 4 0 014-4h4a4 4 0 014 4v2"/>
                <line x1="19" y1="8" x2="19" y2="14"/>
                <line x1="22" y1="11" x2="16" y2="11"/>
              </svg>
            </div>
            <span className={`flex-1 text-left text-sm sm:text-base ${isPremium ? 'text-white' : 'text-text-primary'}`}>Заказ другому человеку</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={isPremium ? 'text-text-muted' : 'text-text-muted'}>
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </button>

          {/* Запланировать */}
          <button 
            onClick={() => setShowScheduleModal(true)}
            className={`w-full flex items-center gap-4 px-4 py-4 border-b ${isPremium ? 'border-white/10' : 'border-primary/10'} transition-colors`}
          >
            <div className={`w-10 h-10 rounded-full ${isPremium ? 'bg-dark-modal' : 'bg-dark-secondary'} flex items-center justify-center ${isPremium ? 'text-text-secondary' : 'text-text-secondary'}`}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
            </div>
            <div className="flex-1 text-left">
              <span className={`text-sm sm:text-base ${isPremium ? 'text-white' : 'text-text-primary'}`}>Запланировать поездку</span>
              {scheduledDate && (
                <p className={`text-xs ${isPremium ? 'text-amber-400' : 'text-primary'}`}>
                  {scheduledDate.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })} в {scheduledDate.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
            </div>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={isPremium ? 'text-text-muted' : 'text-text-muted'}>
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </button>

          {/* Специальные возможности */}
          <button 
            onClick={() => setShowAccessibilityModal(true)}
            className="w-full flex items-center gap-4 px-4 py-4 transition-colors"
          >
            <div className={`w-10 h-10 rounded-full ${isPremium ? 'bg-dark-modal' : 'bg-dark-secondary'} flex items-center justify-center ${isPremium ? 'text-text-secondary' : 'text-text-secondary'}`}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="4" r="2"/>
                <path d="M12 8v8"/>
                <path d="M8 12h8"/>
                <path d="M9 20l3-4 3 4"/>
              </svg>
            </div>
            <div className="flex-1 text-left">
              <p className={`text-sm sm:text-base ${isPremium ? 'text-white' : 'text-text-primary'}`}>Специальные возможности</p>
              <p className={`text-[10px] sm:text-xs ${isPremium ? 'text-text-muted' : 'text-text-muted'}`}>Инвалидное кресло, помощь в поиске машины и...</p>
            </div>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={isPremium ? 'text-text-muted' : 'text-text-muted'}>
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Кнопка заказа */}
      <div className={`px-4 py-3 sm:py-4 pb-safe ${isPremium ? 'bg-dark' : 'bg-dark-tertiary'} border-t ${isPremium ? 'border-white/10' : 'border-primary/10'} flex items-center gap-3`}>
        {/* Оплата */}
        <button className={`w-12 h-12 rounded-xl ${isPremium ? 'bg-dark-modal border-white/10' : 'bg-dark-secondary border-primary/10'} border flex items-center justify-center`}>
          <svg width="20" height="20" viewBox="0 0 32 32" fill="none">
            <circle cx="16" cy="16" r="14" fill="#26a17b"/>
            <path d="M17.5 17.5V22H14.5V17.5H9V15H14.5V13.5C11.5 13.2 9.5 12.5 9.5 11.5C9.5 10.3 12.5 9.5 16 9.5C19.5 9.5 22.5 10.3 22.5 11.5C22.5 12.5 20.5 13.2 17.5 13.5V15H23V17.5H17.5Z" fill="white"/>
          </svg>
        </button>
        
        <button
          onClick={onOrder}
          className={`flex-1 py-3.5 sm:py-4 rounded-xl font-semibold text-base sm:text-lg ${theme.buttonBg} text-white shadow-lg hover:opacity-90 active:scale-[0.98] transition-all`}
        >
          {isPremium ? 'Выбрать кресло' : 'Заказать'}
        </button>
        
        {/* Развернуть */}
        <button className={`w-12 h-12 rounded-xl ${isPremium ? 'bg-dark-modal border-white/10' : 'bg-dark-secondary border-primary/10'} border flex items-center justify-center ${isPremium ? 'text-text-muted' : 'text-text-secondary'}`}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 9l6 6 6-6"/>
          </svg>
        </button>
      </div>

      {/* Модальные окна */}
      <CommentModal
        isOpen={showCommentModal}
        onClose={() => setShowCommentModal(false)}
        value={comment}
        onChange={setComment}
      />

      <ScheduleModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        selectedDate={scheduledDate}
        onDateChange={setScheduledDate}
      />

      <AccessibilityModal
        isOpen={showAccessibilityModal}
        onClose={() => setShowAccessibilityModal(false)}
        options={accessibilityOptions}
        onOptionsChange={setAccessibilityOptions}
      />
    </div>
  )
}

export default TariffDetails
